import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { customSelectStylesLarge } from '../../../config/selectStyles';
import ColorPickerPopup from '../../ui/ColorPickerPopup';
import AppearancePresetSelector from '../../ui/AppearancePresetSelector';
import { FONT_OPTIONS, FORM_STYLE_OPTIONS, BUTTON_STYLE_OPTIONS, getFontOptionsWithCustom, getFontFamilyCSS } from '../../Settings/Forms/formsConstants';
import { Save } from 'lucide-react';

const NEWSLETTER_DEFAULTS = {
    signup_font: '1',
    signup_form_style: '1',
    signup_button_style: '1',
    signup_background_color: '#ffffff',
    signup_text_color: '#2c3e50',
    signup_button_color: '#5b7c8d',
    signup_button_text_color: '#ffffff',
    signup_form_field_color: '#ffffff',
    signup_form_field_border: '#cbd5e1',
    signup_link_text_color: '#5b7c8d',
};

const GroupAppearanceTab = ({ groupId, showAlert }) => {
    const [settings, setSettings] = useState({ ...NEWSLETTER_DEFAULTS });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // #123b — eigene Schrift des Mandanten in die Auswahl aufnehmen
    const [customFont, setCustomFont] = useState(null);
    useEffect(() => {
        fetch('index.php/settings/getCustomFont', { credentials: 'same-origin' })
            .then(r => r.json()).then(setCustomFont).catch(() => {});
    }, []);
    const fontOptions = getFontOptionsWithCustom(customFont);

    const fetchSettings = useCallback(async () => {
        try {
            const res = await fetch(`index.php/groups/getGroupSettings?group_id=${groupId}`);
            const data = await res.json();
            if (data.success && data.data) {
                setSettings(prev => ({
                    ...prev,
                    signup_font: String(data.data.signup_font ?? NEWSLETTER_DEFAULTS.signup_font),
                    signup_form_style: String(data.data.signup_form_style ?? NEWSLETTER_DEFAULTS.signup_form_style),
                    signup_button_style: String(data.data.signup_button_style ?? NEWSLETTER_DEFAULTS.signup_button_style),
                    signup_background_color: data.data.signup_background_color ?? NEWSLETTER_DEFAULTS.signup_background_color,
                    signup_text_color: data.data.signup_text_color ?? NEWSLETTER_DEFAULTS.signup_text_color,
                    signup_button_color: data.data.signup_button_color ?? NEWSLETTER_DEFAULTS.signup_button_color,
                    signup_button_text_color: data.data.signup_button_text_color ?? NEWSLETTER_DEFAULTS.signup_button_text_color,
                    signup_form_field_color: data.data.signup_form_field_color ?? NEWSLETTER_DEFAULTS.signup_form_field_color,
                    signup_form_field_border: data.data.signup_form_field_border ?? NEWSLETTER_DEFAULTS.signup_form_field_border,
                    signup_link_text_color: data.data.signup_link_text_color ?? NEWSLETTER_DEFAULTS.signup_link_text_color,
                }));
            }
        } catch (err) {
            console.error('Fehler beim Laden der Design-Einstellungen:', err);
        } finally {
            setLoading(false);
        }
    }, [groupId]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('group_id', groupId);
            Object.entries(settings).forEach(([key, value]) => {
                formData.append(key, value);
            });

            const res = await fetch('index.php/groups/updateGroupSettings', {
                method: 'POST',
                body: formData,
            });
            const data = await res.json();
            if (data.success) {
                showAlert('Design-Einstellungen gespeichert', 'success');
            } else {
                showAlert(data.error || 'Fehler beim Speichern', 'danger');
            }
        } catch (err) {
            showAlert('Fehler beim Speichern der Einstellungen', 'danger');
        } finally {
            setSaving(false);
        }
    };

    // Helper: Font-Name aus Value
    // Liefert die CSS font-family fuer die Live-Vorschau (#123b: inkl. eigener Schrift)
    const getFontName = (value) => getFontFamilyCSS(value);

    // Helper: Input border-radius aus Form-Style
    const getFormBorderRadius = (style) => {
        switch (String(style)) {
            case '1': return '8px';
            case '2': return '0px';
            case '3': return '0px';
            default: return '8px';
        }
    };

    // Helper: Button border-radius aus Button-Style
    const getButtonBorderRadius = (style) => {
        return String(style) === '1' ? '20px' : '4px';
    };

    const labelStyle = {
        fontSize: '0.875rem',
        fontWeight: '500',
        color: 'var(--text-secondary, #64748b)',
        marginBottom: '0.5rem',
        display: 'block'
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-secondary" role="status">
                    <span className="visually-hidden">Laden...</span>
                </div>
            </div>
        );
    }

    // Map signup_ prefix fields to generic preset fields for currentValues
    const presetCurrentValues = {
        font: settings.signup_font,
        form_style: settings.signup_form_style,
        button_style: settings.signup_button_style,
        background_color: settings.signup_background_color || NEWSLETTER_DEFAULTS.signup_background_color,
        text_color: settings.signup_text_color || NEWSLETTER_DEFAULTS.signup_text_color,
        button_color: settings.signup_button_color || NEWSLETTER_DEFAULTS.signup_button_color,
        button_text_color: settings.signup_button_text_color || NEWSLETTER_DEFAULTS.signup_button_text_color,
        form_field_color: settings.signup_form_field_color || NEWSLETTER_DEFAULTS.signup_form_field_color,
        form_field_border: settings.signup_form_field_border || NEWSLETTER_DEFAULTS.signup_form_field_border,
        link_text_color: settings.signup_link_text_color || NEWSLETTER_DEFAULTS.signup_link_text_color,
    };

    return (
        <div>
            {/* Preset Selector */}
            <AppearancePresetSelector
                onApplyPreset={(presetData) => {
                    // Map generic preset fields to signup_ prefix fields
                    const fieldMap = {
                        font: 'signup_font',
                        form_style: 'signup_form_style',
                        button_style: 'signup_button_style',
                        background_color: 'signup_background_color',
                        text_color: 'signup_text_color',
                        button_color: 'signup_button_color',
                        button_text_color: 'signup_button_text_color',
                        form_field_color: 'signup_form_field_color',
                        form_field_border: 'signup_form_field_border',
                        link_text_color: 'signup_link_text_color',
                    };
                    Object.entries(presetData).forEach(([key, value]) => {
                        const mappedKey = fieldMap[key] || key;
                        handleChange(mappedKey, value);
                    });
                }}
                currentValues={presetCurrentValues}
                includeLinks={true}
            />

            {/* Style Settings */}
            <div className="row g-4 mb-4">
                <div className="col-md-4">
                    <label style={labelStyle}>Schriftart</label>
                    <Select
                        value={fontOptions.find(opt => opt.value === String(settings.signup_font))}
                        onChange={(selected) => handleChange('signup_font', selected?.value)}
                        options={fontOptions}
                        styles={customSelectStylesLarge}
                        classNamePrefix="select"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                </div>
                <div className="col-md-4">
                    <label style={labelStyle}>Formstil</label>
                    <Select
                        value={FORM_STYLE_OPTIONS.find(opt => opt.value === String(settings.signup_form_style))}
                        onChange={(selected) => handleChange('signup_form_style', selected?.value)}
                        options={FORM_STYLE_OPTIONS}
                        styles={customSelectStylesLarge}
                        classNamePrefix="select"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                </div>
                <div className="col-md-4">
                    <label style={labelStyle}>Buttonstil</label>
                    <Select
                        value={BUTTON_STYLE_OPTIONS.find(opt => opt.value === String(settings.signup_button_style))}
                        onChange={(selected) => handleChange('signup_button_style', selected?.value)}
                        options={BUTTON_STYLE_OPTIONS}
                        styles={customSelectStylesLarge}
                        classNamePrefix="select"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                </div>
            </div>

            {/* Color Settings - Row 1 */}
            <div className="row g-4 mb-4">
                <div className="col-md-4">
                    <ColorPickerPopup
                        label="Hintergrundfarbe"
                        color={settings.signup_background_color || NEWSLETTER_DEFAULTS.signup_background_color}
                        onChange={(color) => handleChange('signup_background_color', color)}
                    />
                </div>
                <div className="col-md-4">
                    <ColorPickerPopup
                        label="Textfarbe"
                        color={settings.signup_text_color || NEWSLETTER_DEFAULTS.signup_text_color}
                        onChange={(color) => handleChange('signup_text_color', color)}
                    />
                </div>
                <div className="col-md-4">
                    <ColorPickerPopup
                        label="Buttonfarbe"
                        color={settings.signup_button_color || NEWSLETTER_DEFAULTS.signup_button_color}
                        onChange={(color) => handleChange('signup_button_color', color)}
                    />
                </div>
            </div>

            {/* Color Settings - Row 2 */}
            <div className="row g-4 mb-4">
                <div className="col-md-4">
                    <ColorPickerPopup
                        label="Buttontextfarbe"
                        color={settings.signup_button_text_color || NEWSLETTER_DEFAULTS.signup_button_text_color}
                        onChange={(color) => handleChange('signup_button_text_color', color)}
                    />
                </div>
                <div className="col-md-4">
                    <ColorPickerPopup
                        label="Formfeldfarbe"
                        color={settings.signup_form_field_color || NEWSLETTER_DEFAULTS.signup_form_field_color}
                        onChange={(color) => handleChange('signup_form_field_color', color)}
                    />
                </div>
                <div className="col-md-4">
                    <ColorPickerPopup
                        label="Formfeldrahmen"
                        color={settings.signup_form_field_border || NEWSLETTER_DEFAULTS.signup_form_field_border}
                        onChange={(color) => handleChange('signup_form_field_border', color)}
                    />
                </div>
            </div>

            {/* Color Settings - Row 3 */}
            <div className="row g-4 mb-4">
                <div className="col-md-4">
                    <ColorPickerPopup
                        label="Linkfarbe"
                        color={settings.signup_link_text_color || NEWSLETTER_DEFAULTS.signup_link_text_color}
                        onChange={(color) => handleChange('signup_link_text_color', color)}
                    />
                </div>
            </div>

            {/* Live Preview */}
            <div className="mt-5">
                <h6 className="fw-semibold mb-3" style={{ color: 'var(--text-primary, #2c3e50)' }}>
                    Vorschau
                </h6>
                <div
                    style={{
                        backgroundColor: settings.signup_background_color || NEWSLETTER_DEFAULTS.signup_background_color,
                        padding: '2rem',
                        borderRadius: '12px',
                        border: '1px solid var(--border-light, #e2e8f0)',
                        maxWidth: '480px',
                    }}
                >
                    {/* Title */}
                    <h4 style={{
                        fontFamily: getFontName(settings.signup_font),
                        color: settings.signup_text_color || NEWSLETTER_DEFAULTS.signup_text_color,
                        marginBottom: '0.5rem',
                        fontSize: '1.5rem',
                        fontWeight: 600,
                    }}>
                        Newsletter anmelden
                    </h4>
                    <p style={{
                        fontFamily: getFontName(settings.signup_font),
                        color: settings.signup_text_color || NEWSLETTER_DEFAULTS.signup_text_color,
                        opacity: 0.75,
                        marginBottom: '1.5rem',
                        fontSize: '0.95rem',
                    }}>
                        Melde dich an und bleib immer auf dem Laufenden.
                    </p>

                    {/* Name Row */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <label style={{
                                fontFamily: getFontName(settings.signup_font),
                                color: settings.signup_text_color || NEWSLETTER_DEFAULTS.signup_text_color,
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                marginBottom: '0.375rem',
                                display: 'block',
                            }}>
                                Vorname *
                            </label>
                            <input
                                type="text"
                                placeholder="Maria"
                                disabled
                                style={{
                                    fontFamily: getFontName(settings.signup_font),
                                    backgroundColor: settings.signup_form_field_color || NEWSLETTER_DEFAULTS.signup_form_field_color,
                                    color: settings.signup_text_color || NEWSLETTER_DEFAULTS.signup_text_color,
                                    border: String(settings.signup_form_style) === '3'
                                        ? 'none'
                                        : `1px solid ${settings.signup_form_field_border || NEWSLETTER_DEFAULTS.signup_form_field_border}`,
                                    borderBottom: String(settings.signup_form_style) === '3'
                                        ? `1px solid ${settings.signup_form_field_border || NEWSLETTER_DEFAULTS.signup_form_field_border}`
                                        : undefined,
                                    borderRadius: getFormBorderRadius(settings.signup_form_style),
                                    padding: '10px 14px',
                                    width: '100%',
                                    fontSize: '0.95rem',
                                    outline: 'none',
                                }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{
                                fontFamily: getFontName(settings.signup_font),
                                color: settings.signup_text_color || NEWSLETTER_DEFAULTS.signup_text_color,
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                marginBottom: '0.375rem',
                                display: 'block',
                            }}>
                                Nachname
                            </label>
                            <input
                                type="text"
                                placeholder="Muster"
                                disabled
                                style={{
                                    fontFamily: getFontName(settings.signup_font),
                                    backgroundColor: settings.signup_form_field_color || NEWSLETTER_DEFAULTS.signup_form_field_color,
                                    color: settings.signup_text_color || NEWSLETTER_DEFAULTS.signup_text_color,
                                    border: String(settings.signup_form_style) === '3'
                                        ? 'none'
                                        : `1px solid ${settings.signup_form_field_border || NEWSLETTER_DEFAULTS.signup_form_field_border}`,
                                    borderBottom: String(settings.signup_form_style) === '3'
                                        ? `1px solid ${settings.signup_form_field_border || NEWSLETTER_DEFAULTS.signup_form_field_border}`
                                        : undefined,
                                    borderRadius: getFormBorderRadius(settings.signup_form_style),
                                    padding: '10px 14px',
                                    width: '100%',
                                    fontSize: '0.95rem',
                                    outline: 'none',
                                }}
                            />
                        </div>
                    </div>

                    {/* Email */}
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{
                            fontFamily: getFontName(settings.signup_font),
                            color: settings.signup_text_color || NEWSLETTER_DEFAULTS.signup_text_color,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            marginBottom: '0.375rem',
                            display: 'block',
                        }}>
                            E-Mail-Adresse *
                        </label>
                        <input
                            type="email"
                            placeholder="maria@beispiel.de"
                            disabled
                            style={{
                                fontFamily: getFontName(settings.signup_font),
                                backgroundColor: settings.signup_form_field_color || NEWSLETTER_DEFAULTS.signup_form_field_color,
                                color: settings.signup_text_color || NEWSLETTER_DEFAULTS.signup_text_color,
                                border: String(settings.signup_form_style) === '3'
                                    ? 'none'
                                    : `1px solid ${settings.signup_form_field_border || NEWSLETTER_DEFAULTS.signup_form_field_border}`,
                                borderBottom: String(settings.signup_form_style) === '3'
                                    ? `1px solid ${settings.signup_form_field_border || NEWSLETTER_DEFAULTS.signup_form_field_border}`
                                    : undefined,
                                borderRadius: getFormBorderRadius(settings.signup_form_style),
                                padding: '10px 14px',
                                width: '100%',
                                fontSize: '0.95rem',
                                outline: 'none',
                            }}
                        />
                    </div>

                    {/* Privacy Checkbox */}
                    <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <input type="checkbox" disabled style={{ marginTop: '3px', width: '16px', height: '16px' }} />
                        <span style={{
                            fontFamily: getFontName(settings.signup_font),
                            color: settings.signup_text_color || NEWSLETTER_DEFAULTS.signup_text_color,
                            opacity: 0.75,
                            fontSize: '0.875rem',
                            lineHeight: 1.5,
                        }}>
                            Ich habe die{' '}
                            <a
                                href="#"
                                style={{
                                    color: settings.signup_link_text_color || NEWSLETTER_DEFAULTS.signup_link_text_color,
                                    textDecoration: 'underline',
                                }}
                                onClick={(e) => e.preventDefault()}
                            >
                                Datenschutzerklärung
                            </a>{' '}
                            gelesen und bin einverstanden. *
                        </span>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="button"
                        disabled
                        style={{
                            fontFamily: getFontName(settings.signup_font),
                            backgroundColor: settings.signup_button_color || NEWSLETTER_DEFAULTS.signup_button_color,
                            color: settings.signup_button_text_color || NEWSLETTER_DEFAULTS.signup_button_text_color,
                            border: 'none',
                            borderRadius: getButtonBorderRadius(settings.signup_button_style),
                            padding: '12px 20px',
                            width: '100%',
                            fontSize: '1rem',
                            fontWeight: 500,
                            cursor: 'default',
                        }}
                    >
                        Jetzt anmelden
                    </button>
                </div>
            </div>

            {/* Save Button */}
            <div className="d-flex justify-content-end mt-4">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="btn btn-primary d-flex align-items-center gap-2"
                    style={{
                        padding: '0.625rem 1.5rem',
                        borderRadius: '8px',
                        fontWeight: 500,
                    }}
                >
                    <Save size={18} />
                    {saving ? 'Speichern...' : 'Speichern'}
                </button>
            </div>
        </div>
    );
};

export default GroupAppearanceTab;
